import { useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import FAQHero from './components/FAQHero';
import FAQContent from './components/FAQContent';
import ServiceAreaMap from './components/ServiceAreaMap';
import ComparisonTable from './components/ComparisonTable';

export default function FAQ() {
  useEffect(() => {
    // Update meta tags
    document.title =
      'Areas We Serve - Vancouver, Burnaby, Surrey, Coquitlam, Langley | Dinari Designs';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Professional AutoCAD layout and formwork detailing services across the Lower Mainland: Vancouver, Burnaby, Surrey, Coquitlam, New Westminster, Langley, Richmond, and Delta. Expert construction technical documentation for BC projects.'
      );
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute(
        'content',
        'Vancouver construction services, Burnaby formwork, Surrey layout, Coquitlam detailing, Langley construction, Richmond AutoCAD, Delta technical drawings, Lower Mainland construction'
      );
    }

    // Add Schema.org JSON-LD for FAQPage
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How long does it take to get a construction layout quote in Vancouver?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'At Dinari Designs, we provide professional layout and formwork quotes within 24-48 hours for projects across the Lower Mainland and Victoria.',
          },
        },
        {
          '@type': 'Question',
          name: 'What software is used for professional formwork detailing?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'We utilize industry-standard AutoCAD for high-precision 2D drafting and Bluebeam Revu for accurate material takeoffs and digital document coordination.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is it better to hire a full-time layout specialist or a contractor?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'For most firms, hiring a specialized contractor like Dinari Designs is more cost-effective. It eliminates the overhead of full-time salaries, benefits, and software licenses while providing on-demand expertise.',
          },
        },
        {
          '@type': 'Question',
          name: 'What cities in the Lower Mainland do you serve?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Dinari Designs provides professional construction layout and formwork services across the entire Lower Mainland, including Vancouver, Burnaby, Surrey, Coquitlam, New Westminster, Langley, Richmond, and Delta.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do you provide construction layout services in Coquitlam and New Westminster?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Dinari Designs offers expert AutoCAD layout and formwork detailing for high-density and residential projects throughout Coquitlam and New Westminster. We understand the unique terrain challenges and municipal requirements of these hillside and urban riverfront areas.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can you support new developments in Langley and the Fraser Valley?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Absolutely. With the rapid expansion in Langley and the surrounding Fraser Valley, we provide on-demand technical detailing to help developers keep pace with tight timelines. Our contract-based model is ideal for firms looking to scale their capacity without adding full-time staff.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is the benefit of hiring a contract specialist for Lower Mainland projects?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'By contracting your detailing to Dinari Designs, you eliminate the overhead costs associated with a full-time employee—including benefits, payroll taxes, and AutoCAD licenses. This Lean Construction approach allows firms in Vancouver, Surrey, and Burnaby to stay competitive and agile.',
          },
        },
        {
          '@type': 'Question',
          name: 'How fast can I get a layout quote for a project in Richmond or Delta?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "We offer a 24-48 hour turnaround for project quotes across Richmond, Delta, and the entire Lower Mainland. Our goal is to ensure your project doesn't stall waiting for technical documentation.",
          },
        },
        {
          '@type': 'Question',
          name: 'How does contracting layout work save money for developers in Coquitlam and Langley?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Hiring a contract specialist eliminates the high fixed costs of a full-time employee, such as benefits, workstation hardware, and annual AutoCAD software licenses, allowing for a more flexible project budget.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do you offer on-site consultations in New Westminster and the Fraser Valley?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, we offer on-demand technical consultations and site planning for projects in New Westminster, Langley, and throughout the Fraser Valley.',
          },
        },
      ],
    });

    // Prevent duplicate schema scripts
    if (!document.head.querySelector('script[data-schema="faq"]')) {
      script.setAttribute('data-schema', 'faq');
      document.head.appendChild(script);
    }

    // Cleanup on unmount
    return () => {
      const schemaScript = document.head.querySelector('script[data-schema="faq"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FAQHero />
      <ServiceAreaMap />
      <FAQContent />
      <ComparisonTable />
      <Footer />
    </div>
  );
}
